/**
 * SetupVpostConnectionUseCase
 *
 * Use case for setting up an OCPI-to-VPOST connection for an external CPO operator.
 * Calls PUT /external-ocpi-cpo/{id}/connections/ocpi-to-vpost
 *
 * Same validation as SetupExternalCpoConnectionUseCase, with the addition of csms_name.
 */

import type { OcpiAdminRepository } from '../data/ocpi-admin-repository';
import type { VpostConnectionRequestDto, OcpiVersion } from '../types/api-types';

const VALID_OCPI_VERSIONS: OcpiVersion[] = ['2.1.1', '2.2', '2.2.1'];

export class SetupVpostConnectionUseCase {
  constructor(private readonly repository: OcpiAdminRepository) {}

  async execute(
    operatorId: number,
    data: VpostConnectionRequestDto
  ): Promise<void> {
    this.validateInput(data);
    await this.repository.setupVpostConnection(operatorId, data);
  }

  private validateInput(data: VpostConnectionRequestDto): void {
    if (!data.connection_version) {
      throw new Error('OCPI version is required');
    }

    if (!VALID_OCPI_VERSIONS.includes(data.connection_version)) {
      throw new Error(
        `Invalid OCPI version. Must be one of: ${VALID_OCPI_VERSIONS.join(', ')}`
      );
    }

    if (data.versions_url) {
      try {
        new URL(data.versions_url);
      } catch {
        throw new Error('Invalid versions URL format. Must be a valid URL.');
      }
    }

    if (data.token_a !== undefined && data.token_a.trim() === '') {
      throw new Error('Token A cannot be empty if provided');
    }

    if (!data.csms_name || data.csms_name.trim() === '') {
      throw new Error('CSMS name is required');
    }
  }
}
